import {
  ECurrencySymbolDisplay,
  EFontFamily,
  EFontSize,
  EFooterAlignment,
  EMarginSize,
  EPageOrientation,
  EShowPageSetting
} from '../types';

type ActionTypeValues = (typeof actionTypes)[keyof typeof actionTypes];
type ActionTypeMapping = {
  [key in keyof IActionsPDF]: string;
};
type ActionPayload<T> = T extends (payload: infer P) => void ? P : never;

export interface IActionsPDF {
  setPageOrientation(payload: EPageOrientation): void;
  setMarginSize(payload: EMarginSize): void;
  setFontFamily(payload: EFontFamily): void;
  toggleShowHeader(): void;
  setShowPageSetting(payload: EShowPageSetting): void;
  setPrimaryTitle(payload: string): void;
  setSecondaryTitle(payload: string): void;
  setSubtitle(payload: string): void;
  toggleShowPrimaryTitle(): void;
  toggleShowSecondaryTitle(): void;
  toggleShowSubtitle(): void;
  toggleShowNumberFormattingThousands(): void;
  toggleShowAppliedFilters(): void;
  setShowPdfLoadingSpinner(payload: boolean): void;
  setPreviewCount(payload: number): void;
  setPreviewLink(payload: string): void;
  setFirstRender(): void;
  setPreviewTimerId(payload: string): void;
  toggleShowFooter(): void;
  setNote(payload: string): void;
  toggleShowNote(): void;
  toggleShowPageNumber(): void;
  toggleShowExportTimeStamp(): void;
  setSummaryTableFontSize(payload: EFontSize): void;
  setColumnsPerPage(payload: number): void;
  setShowGridlines(): void;
  setMaxColumnWidth(payload: number): void;
  setPageNumberAlignment(payload: EFooterAlignment): void;
  setNoteAlignment(payload: EFooterAlignment): void;
  setTimestampAlignment(payload: EFooterAlignment): void;
  setCurrencySymbolDisplay(payload: ECurrencySymbolDisplay): void;
  setShowUnderlines(): void;
  toggleKeepRowGroupsTogetherOnPageBreaks(): void;
  toggleKeepColumnGroupsTogetherOnPageBreaks(): void;
}

const actionTypes: ActionTypeMapping = {
  setPageOrientation: 'UPDATE_PAGE_ORIENTATION',
  setMarginSize: 'UPDATE_MARGIN_SIZE',
  setFontFamily: 'UPDATE_FONT_FAMILY',
  toggleShowHeader: 'TOGGLE_HEADER',
  setShowPageSetting: 'UPDATE_SHOW_PAGE_SETTING',
  setPrimaryTitle: 'UPDATE_PRIMARY_TITLE',
  setSecondaryTitle: 'UPDATE_SECONDARY_TITLE',
  setSubtitle: 'UPDATE_SUBTITLE',
  toggleShowPrimaryTitle: 'TOGGLE_SHOW_PRIMARY_TITLE',
  toggleShowSecondaryTitle: 'TOGGLE_SHOW_SECONDARY_TITLE',
  toggleShowSubtitle: 'TOGGLE_SHOW_SUBTITLE',
  toggleShowNumberFormattingThousands: 'TOGGLE_SHOW_NUMBER_FORMATTING_THOUSANDS',
  toggleShowAppliedFilters: 'TOGGLE_SHOW_APPLIED_FILTERS',
  setShowPdfLoadingSpinner: 'UPDATE_SHOW_PDF_LOADING_SPINNER',
  setPreviewCount: 'UPDATE_PREVIEW_COUNT',
  setPreviewLink: 'UPDATE_PREVIEW_LINK',
  setFirstRender: 'SET_FIRST_RENDER',
  setPreviewTimerId: 'UPDATE_PREVIEW_TIMER_ID',
  toggleShowFooter: 'TOGGLE_SHOW_FOOTER',
  setNote: 'UPDATE_NOTE',
  toggleShowNote: 'TOGGLE_SHOW_NOTE',
  toggleShowPageNumber: 'TOGGLE_SHOW_PAGE_NUMBER',
  toggleShowExportTimeStamp: 'TOGGLE_SHOW_EXPORT_TIMESTAMP',
  setSummaryTableFontSize: 'UPDATE_FONT_SIZE',
  setColumnsPerPage: 'UPDATE_COLUMNS_PER_PAGE',
  setShowGridlines: 'TOGGLE_SHOW_GRIDLINES',
  setMaxColumnWidth: 'UPDATE_MAX_COLUMN_WIDTH',
  setPageNumberAlignment: 'UPDATE_PAGE_NUMBER_ALIGNMENT',
  setNoteAlignment: 'UPDATE_NOTE_ALIGNMENT',
  setTimestampAlignment: 'UPDATE_TIMESTAMP_ALIGNMENT',
  setCurrencySymbolDisplay: 'UPDATE_CURRENCY_SYMBOL_DISPLAY',
  setShowUnderlines: 'TOGGLE_SHOW_UNDERLINES',
  toggleKeepRowGroupsTogetherOnPageBreaks: 'TOGGLE_KEEP_ROW_GROUPS_TOGETHER_ON_PAGE_BREAK',
  toggleKeepColumnGroupsTogetherOnPageBreaks: 'TOGGLE_KEEP_COLUMN_GROUPS_TOGETHER_ON_PAGE_BREAK'
};

const generateActionTypes = () => {
  const actionTypesConst: { [key in ActionTypeValues]: key } = {};
  for (const action in actionTypes) {
    const actionType = actionTypes[action as keyof typeof actionTypes];
    actionTypesConst[actionType] = actionType;
  }
  return actionTypesConst;
};

export const {
  UPDATE_PAGE_ORIENTATION,
  UPDATE_MARGIN_SIZE,
  UPDATE_FONT_FAMILY,
  TOGGLE_HEADER,
  UPDATE_SHOW_PAGE_SETTING,
  UPDATE_PRIMARY_TITLE,
  UPDATE_SECONDARY_TITLE,
  UPDATE_SUBTITLE,
  TOGGLE_SHOW_PRIMARY_TITLE,
  TOGGLE_SHOW_SECONDARY_TITLE,
  TOGGLE_SHOW_SUBTITLE,
  TOGGLE_SHOW_NUMBER_FORMATTING_THOUSANDS,
  TOGGLE_SHOW_APPLIED_FILTERS,
  UPDATE_SHOW_PDF_LOADING_SPINNER,
  UPDATE_PREVIEW_COUNT,
  UPDATE_PREVIEW_LINK,
  SET_FIRST_RENDER,
  UPDATE_PREVIEW_TIMER_ID,
  TOGGLE_SHOW_FOOTER,
  UPDATE_NOTE,
  TOGGLE_SHOW_NOTE,
  TOGGLE_SHOW_PAGE_NUMBER,
  TOGGLE_SHOW_EXPORT_TIMESTAMP,
  UPDATE_FONT_SIZE,
  UPDATE_COLUMNS_PER_PAGE,
  TOGGLE_SHOW_GRIDLINES,
  UPDATE_MAX_COLUMN_WIDTH,
  UPDATE_PAGE_NUMBER_ALIGNMENT,
  UPDATE_NOTE_ALIGNMENT,
  UPDATE_TIMESTAMP_ALIGNMENT,
  UPDATE_CURRENCY_SYMBOL_DISPLAY,
  TOGGLE_SHOW_UNDERLINES,
  TOGGLE_KEEP_ROW_GROUPS_TOGETHER_ON_PAGE_BREAK,
  TOGGLE_KEEP_COLUMN_GROUPS_TOGETHER_ON_PAGE_BREAK
} = generateActionTypes();

export const createActions = (dispatch): IActionsPDF => {
  const actions: IActionsPDF = {} as IActionsPDF;
  for (const action in actionTypes) {
    const actionType = actionTypes[action];
    actions[action] = (
      (type: typeof actionType) => (payload: ActionPayload<typeof actionType>) =>
        dispatch({ type, payload })
    )(actionType);
  }
  return actions;
};
